'use client'

import * as React from 'react';
import { 
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  ScrollArea,
  ScrollBar,
  cn
} from '../../../../components/ui';
import { Database, ChevronRight, Code as CodeIcon, Copy, Check } from 'lucide-react';
import { SQLQueryResponse } from '../types';
import { useNovaStoreContext } from '../../nova/nova-store-context';

/**
 * SQL Query Result Component
 * 
 * Displays the result of an SQL query in a formatted table.
 */
export function SqlQueryResult({ 
  result, 
  onRunSqlQuery,
  width,
  height = 300
}: { 
  result: SQLQueryResponse; 
  onRunSqlQuery?: (query: string) => void;
  width: number;
  height?: number;
}) {
  // Log the props for debugging
  
  // State to track if the SQL dialog is open
  const [showSql, setShowSql] = React.useState(false);
  
  // State to track copy button status
  const [copied, setCopied] = React.useState(false);
  
  // Extract the SQL query from the result if available
  const sqlQuery = (result as any).query || 'SQL query not available';
  
  // Calculate column width based on number of columns and available width
  const columnWidth = React.useMemo(() => {
    if (!result.columns || result.columns.length === 0) return 150;
    
    // For tables with any number of columns, ensure they're wide enough to be readable
    const minColumnWidth = 150; // Minimum readable column width
    
    // Calculate a width that ensures the table is wider than the container
    // For 5 columns, we need each column to be at least (width / 5) + 20 = ~118px
    // to ensure the total width is greater than the specified width
    const calculatedWidth = Math.max(minColumnWidth, Math.ceil(width / result.columns.length) + 20);
    
    return calculatedWidth;
  }, [result.columns, width]);
  
  // Calculate total table width
  const tableWidth = React.useMemo(() => {
    if (!result.columns || result.columns.length === 0) return width;
    
    // Calculate total width based on columns
    const totalWidth = result.columns.length * columnWidth;
    
    // Ensure the table is always wider than the specified width to trigger horizontal scrolling
    // but not excessively wide
    const minRequiredWidth = Math.max(width + 50, totalWidth);
    
    return minRequiredWidth;
  }, [result.columns, columnWidth, width]);
  
  // Handle running the SQL query
  const handleRunSql = () => {
    if (onRunSqlQuery && sqlQuery && sqlQuery !== 'SQL query not available') {
      onRunSqlQuery(sqlQuery);
    }
  };
  
  // Handle copying SQL to clipboard
  const handleCopySql = async () => {
    try {
      await navigator.clipboard.writeText(sqlQuery);
      setCopied(true);
      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy SQL query:', err);
    }
  };
  
  if (!result.success) {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-sm text-destructive">
        <p className="font-semibold">Error executing query:</p>
        <p>{result.error}</p>
      </div>
    );
  }

  return (
    <div 
      className="bg-card border border-border rounded-lg shadow-sm" 
      style={{ 
        width: `${width}px`,
        height: `${height}px`, 
        display: 'flex', 
        flexDirection: 'column' 
      }}
    >
      {/* Header */}
      <div className="bg-muted/30 px-3 py-2 border-b flex-shrink-0">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Database className="h-4 w-4 text-muted-foreground mr-2" />
              <span className="text-sm font-medium text-foreground">Query Result</span>
            </div>
            <Badge variant="outline" className="text-xs">
              {result.rowCount} rows
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {onRunSqlQuery && (
              <Button 
                variant="outline" 
                size="sm" 
                className="h-7 gap-1"
                onClick={handleRunSql}
              >
                <ChevronRight className="h-3.5 w-3.5" />
                <span className="text-xs">Run</span>
              </Button>
            )}
            <Dialog open={showSql} onOpenChange={setShowSql}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 gap-1">
                  <CodeIcon className="h-3.5 w-3.5" />
                  <span className="text-xs">Show SQL</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle>SQL Query</DialogTitle>
                </DialogHeader>
                <div className="bg-muted/30 p-3 rounded-md relative">
                  <div className="max-h-[200px] overflow-y-auto">
                    <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                      <pre className="text-sm whitespace-pre pr-8 text-foreground">
                        {sqlQuery}
                      </pre>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className={cn(
                      "absolute top-2 right-2 h-7 w-7 p-0",
                      copied && "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800"
                    )}
                    onClick={handleCopySql}
                    title={copied ? "Copied!" : "Copy to clipboard"}
                  >
                    {copied ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                    <span className="sr-only">{copied ? "Copied" : "Copy"}</span>
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
      
      {/* Table Container */}
      <div className="flex-1 overflow-hidden p-3">
        {result.data && result.data.length > 0 ? (
          <ScrollArea className="h-full w-full border rounded">
            <div style={{ 
              width: `${tableWidth}px`,
              minWidth: '100%'
            }}>
              <table 
                className="border-collapse w-full" 
                style={{ tableLayout: 'fixed' }}
              >
                <colgroup>
                  {result.columns?.map((col) => (
                    <col 
                      key={`col-${col.name}`} 
                      style={{ width: `${columnWidth}px` }} 
                    />
                  ))}
                </colgroup>
                <thead className="bg-card z-10 border-b sticky top-0">
                  <tr>
                    {result.columns?.map((col) => (
                      <th 
                        key={col.name} 
                        className="px-3 py-1.5 text-left border-b font-medium text-sm whitespace-nowrap text-foreground"
                        style={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        title={col.name}
                      >
                        {col.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.data.map((row, i) => (
                    <tr key={i} className="hover:bg-muted/50">
                      {result.columns?.map((col) => {
                        const cellValue = String(row[col.name as keyof typeof row] ?? '');
                        return (
                          <td 
                            key={col.name} 
                            className="px-3 py-1.5 border-b whitespace-nowrap text-foreground"
                            style={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            title={cellValue}
                          >
                            {cellValue}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            No data returned
          </div>
        )}
      </div>
    </div>
  );
} 